import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { auth } from "../../lib/firebase";
import { useLang } from "../../lib/useLang";
import { isBackendConfigured } from "../../lib/catalog";
import DailyMenuAdmin from "./DailyMenuAdmin";
import OrdersBoard from "./OrdersBoard";
import StandingMenuAdmin from "./StandingMenuAdmin";

type Tab = "daily" | "orders" | "standing";

export default function AdminPage() {
  const { t, lang, toggle } = useLang();
  const [tab, setTab] = useState<Tab>("daily");

  // Auth state (backend mode only). Demo mode is open.
  const [user, setUser] = useState<User | null>(null);
  const [isStaff, setIsStaff] = useState(!isBackendConfigured);
  const [ready, setReady] = useState(!isBackendConfigured);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isBackendConfigured) return;
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const token = await u.getIdTokenResult();
        setIsStaff(token.claims.role === "staff");
      } else {
        setIsStaff(false);
      }
      setReady(true);
    });
  }, []);

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch {
      setError(t("checkout.required"));
    }
  }

  const authed = !isBackendConfigured || (user && isStaff);

  return (
    <div className="min-h-full bg-cream">
      <header className="bg-brand text-white">
        <div className="mx-auto flex max-w-3xl lg:max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/" className="font-extrabold">
            {t("app.name")} · {t("admin.title")}
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={toggle} className="rounded-lg border border-white/30 px-2 py-1 text-sm">
              {lang === "ar" ? "EN" : "عربي"}
            </button>
            {isBackendConfigured && user && (
              <button
                onClick={() => signOut(auth)}
                className="rounded-lg border border-white/30 px-2 py-1 text-sm"
              >
                {t("admin.signOut")}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl lg:max-w-5xl px-4 py-5">
        {!ready ? (
          <p className="text-black/50">{t("common.loading")}</p>
        ) : !authed ? (
          <form onSubmit={signIn} className="card mx-auto max-w-sm space-y-3 p-6">
            <h2 className="text-lg font-bold">{t("admin.signIn")}</h2>
            <input
              className="input"
              type="email"
              placeholder={t("admin.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              className="input"
              type="password"
              placeholder={t("admin.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <p className="text-sm text-brand">{error}</p>}
            {user && !isStaff && (
              <p className="text-sm text-brand">Not a staff account.</p>
            )}
            <button className="btn-primary w-full">{t("admin.signIn")}</button>
          </form>
        ) : (
          <>
            {!isBackendConfigured && (
              <p className="mb-4 rounded-xl bg-gold/20 p-3 text-sm text-amber-800">
                {t("admin.demoNote")}
              </p>
            )}

            {/* Tabs */}
            <div className="mb-4 flex gap-2">
              {(["daily", "orders", "standing"] as Tab[]).map((tb) => (
                <button
                  key={tb}
                  onClick={() => setTab(tb)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                    tab === tb ? "bg-brand text-white" : "bg-white ring-1 ring-black/10"
                  }`}
                >
                  {t(`admin.${tb === "daily" ? "daily" : tb === "orders" ? "orders" : "standing"}`)}
                </button>
              ))}
            </div>

            {tab === "daily" && <DailyMenuAdmin />}
            {tab === "orders" && <OrdersBoard />}
            {tab === "standing" && <StandingMenuAdmin />}
          </>
        )}
      </div>
    </div>
  );
}
